import { ILongSteelOrderCache } from './types';
import {
  ELongSteelOrdersStatus,
  EOrderTab,
  IBilledOrderData,
  IQCFileResponse,
  IDownloadNFEResponse,
  IOpenOrderData,
  ILongSteelOrderData,
  IOrderDivision,
  ILongSteelOrdersResponse,
  ITrackingFlatSteel,
  IBilledOrderTracking,
  IBilledOrderConditions,
  IFutureDelivery,
  ITrackingLongSteel,
  IUpdateOrderValidation,
  IUpdateOrderResponse,
  IOrdersChangeData,
  IOrdersChageDataResponse,
  IUpdateOrderValidationResponse,
  IUpdateOrderRequest,
  ICgCertificateRequest,
  CgCertificateStatus,
  ICgCertificateResponse
} from '../../store/ducks/long-steel-orders/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import { ELocalStorageKey } from '../EGerdauService/types';
import moment from 'moment';
import { ICustomer } from '../../store/ducks/customers/types';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import { UnleashClient, IToggle } from '@unleash/proxy-client-react';
import { unleashProxyConfig } from '../../unleash-proxy';
import { getAllItems, addItem, cleanStore } from '../../utils/IndexedDB';
import { StoreNames } from '../../utils/IndexedDB/StoreNames';

export class LongSteelOrdersService extends EGerdauService {
  readonly localStorageKey = ELocalStorageKey.LONG_STEEL_ORDERS;
  readonly unleash: UnleashClient;
  constructor() {
    super();
    this.unleash = new UnleashClient(unleashProxyConfig);
  }

  // chamar somente quando carregar
  async getOrdersFromCache(): Promise<ILongSteelOrdersResponse> {
    const cacheData = await this.getOrdersFromClientSideCache();
    if (
      cacheData?.orders !== undefined &&
      new Date().getTime() - cacheData.datetime <= 3600000
    ) {
      return cacheData.orders;
    } else {
      return {
        items: [],
        receivers: [],
        initialInputValues: {
          states: [],
          emitterIds: [],
          receiverId: '',
          startDate: undefined,
          endDate: undefined,
          orderStatus: ELongSteelOrdersStatus.ALL,
          tab: EOrderTab.OPEN
        },
        divisions: [],
        lastLoadTime: undefined
      };
    }
  }

  async getOrdersFromClientSideCache(): Promise<ILongSteelOrderCache> {
    const orders = await getAllItems<ILongSteelOrderCache>(
      StoreNames.LongSteelOrders
    );

    return orders[0];
  }

  async getOrders(
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    tab: EOrderTab,
    startDate: string,
    endDate: string
  ): Promise<ILongSteelOrdersResponse> {
    switch (tab) {
      case EOrderTab.BILLED:
        return this.getBilledOrders(
          states,
          customerIds,
          customerViewType,
          tab,
          startDate,
          endDate
        );

      case EOrderTab.OPEN:
      default:
        return this.getOpenOrders(
          states,
          customerIds,
          customerViewType,
          tab,
          startDate,
          endDate
        );
    }
  }

  private async getOpenOrders(
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    tab: EOrderTab,
    startDate: string,
    endDate: string
  ): Promise<ILongSteelOrdersResponse> {
    const headers = super.getHeaders();
    let response;
    const ordersResponse: ILongSteelOrdersResponse = {
      items: [],
      receivers: [],
      divisions: [],
      lastLoadTime: new Date().getTime(),
      initialInputValues: {
        states: states,
        emitterIds: [],
        receiverId: '',
        startDate,
        endDate,
        tab,
        orderStatus: ELongSteelOrdersStatus.ALL
      }
    };

    if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      const requestURL = ECustomerAuthorizationEndpoints.RECEIVER_OPEN_ORDERS;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as ILongSteelOrderData[][]
      );
      ordersResponse.emitters = this.getEmitters(ordersResponse.items);
    } else {
      const requestURL = ECustomerAuthorizationEndpoints.EMITTER_OPEN_ORDERS;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as ILongSteelOrderData[][]
      );
      ordersResponse.receivers = this.getReceivers(ordersResponse.items);
    }

    ordersResponse.divisions = this.getDivisionsFromOpenOrders(
      ordersResponse.items as IOpenOrderData[]
    );
    ordersResponse.bpError = false;

    this.startInitialInputValues(
      ordersResponse,
      states,
      customerIds,
      customerViewType,
      tab,
      startDate,
      endDate
    );

    this.sortOpenOrdersItemsByQuantities(
      ordersResponse.items as IOpenOrderData[]
    );

    this.sortFutureDeliveriesByEstimatedDeliveryDate(
      ordersResponse.items as IOpenOrderData[]
    );

    await this.setCachedOrders(ordersResponse, tab, undefined);
    return ordersResponse;
  }

  private async setCachedOrders(
    orders: ILongSteelOrdersResponse,
    tab: EOrderTab | undefined,
    customerId?: string
  ) {
    await this.cleanOrders();
    await addItem(StoreNames.LongSteelOrders, {
      orders,
      customerId,
      tab,
      dateTime: new Date().getTime()
    });
  }

  async cleanOrders(): Promise<void> {
    await cleanStore(StoreNames.LongSteelOrders);
  }

  private async getBilledOrders(
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    tab: EOrderTab,
    startDate: string,
    endDate: string
  ): Promise<ILongSteelOrdersResponse> {
    const headers = super.getHeaders();

    let response;
    const ordersResponse: ILongSteelOrdersResponse = {
      items: [],
      receivers: [],
      divisions: [],
      lastLoadTime: new Date().getTime(),
      initialInputValues: {
        states: states,
        emitterIds: [],
        receiverId: '',
        startDate,
        endDate,
        tab,
        orderStatus: ELongSteelOrdersStatus.ALL
      }
    };

    const requestDateFormat = 'YYYYMMDD';
    endDate = moment(endDate, 'YYYY-MM-DD').format(requestDateFormat);
    startDate = moment(startDate, 'YYYY-MM-DD').format(requestDateFormat);

    if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      const requestURL = ECustomerAuthorizationEndpoints.RECEIVER_BILLED_ORDERS;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds, startDate, endDate },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as ILongSteelOrderData[][]
      );
      ordersResponse.emitters = this.getEmitters(ordersResponse.items);
    } else {
      const requestURL = ECustomerAuthorizationEndpoints.EMITTER_BILLED_ORDERS;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds, startDate, endDate },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as ILongSteelOrderData[][]
      );
      ordersResponse.receivers = this.getReceivers(ordersResponse.items);
    }

    this.setCanDownloadQC(ordersResponse.items as IBilledOrderData[]);
    this.setCanDownloadNF(ordersResponse.items as IBilledOrderData[]);
    ordersResponse.divisions = this.getDivisionsFromBilledOrders(
      ordersResponse.items as IBilledOrderData[]
    );
    ordersResponse.bpError = false;

    this.startInitialInputValues(
      ordersResponse,
      states,
      customerIds,
      customerViewType,
      tab,
      moment(startDate, requestDateFormat).format('YYYY-MM-DD'),
      moment(endDate, requestDateFormat).format('YYYY-MM-DD')
    );

    if (!this.doesOrderHasCGItem(ordersResponse.items)) {
      await this.setCachedOrders(ordersResponse, tab, undefined);
    }

    return ordersResponse;
  }

  private mergeArrayOfItems(
    orderResponseItems: ILongSteelOrderData[][]
  ): ILongSteelOrderData[] {
    const items: ILongSteelOrderData[] = [];
    orderResponseItems.forEach((customerOrders) => {
      customerOrders.forEach((order) => {
        items.push(order);
      });
    });
    return items;
  }

  private doesOrderHasCGItem(orderResponseItems: ILongSteelOrderData[]) {
    for (const order of orderResponseItems) {
      const i = order as IBilledOrderData;
      if (
        i.billingHistory.salesOrganization === 'BRCG' ||
        i.billingHistory.salesOrganization === 'BRGO'
      ) {
        return true;
      }
    }
    return false;
  }

  async getBilledOrderTracking(
    item: IBilledOrderData
  ): Promise<IBilledOrderTracking> {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.BILLED_ORDER_TRACKING}/${item.nfeAccessKey}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers
    });

    if (typeof response.data.invoiceAccessKey === 'string') {
      throw new Error(response.data.invoiceAccessKey);
    } else {
      return response.data.invoiceAccessKey as IBilledOrderTracking;
    }
  }

  // retorna os dados de tracking online
  async getTrackingFlatSteel(
    item: IOpenOrderData
  ): Promise<ITrackingFlatSteel> {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.TRACKING_FLAT_STEEL}?salesOrder=${item.salesOrder}&itemNumber=${item.salesOrderItem}&division=${item.division}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers
    });

    return response.data.length > 0
      ? (response.data[0] as ITrackingFlatSteel)
      : ({
          finish: 0,
          meltShop: 0,
          requestedQuantity: 0,
          rollingFinish: 0,
          rollingMill: 0,
          shipment: 0,
          stockWarehouse: 0,
          transit: 0,
          openQuantity: 0
        } as ITrackingFlatSteel);
  }

  // retorna dados de tracking D-1
  async getTrackingFlatSteelExcel(
    items: IOpenOrderData[],
    typeView: ECustomerTypeView
  ) {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.TRACKING_FLAT_STEEL_EXCEL}`;

    const customers: string[] = [];
    const orders: {
      salesOrder: string;
      itemNumber: string;
      division: string;
    }[] = [];

    items.forEach((item) => {
      const itemCode =
        typeView === ECustomerTypeView.EMITTER
          ? item.soldTo.code
          : item.shipTo.code;
      if (!customers.includes(itemCode)) {
        customers.push(itemCode);
      }
      orders.push({
        salesOrder: item.salesOrder.toString(),
        itemNumber: item.salesOrderItem.toString(),
        division: item.division
      });
    });

    const bodyRequest = {
      customers,
      orders
    };
    const response = await customersAuthorizationApi.post(
      requestURL,
      bodyRequest,
      { headers }
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getItemTracking = (responseData: any, item: IOpenOrderData) => {
      const responseDataItemIdx = responseData.findIndex(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (rs: any) =>
          rs.salesOrder === item.salesOrder.toString() &&
          rs.itemNumber === item.salesOrderItem.toString()
      );

      if (responseDataItemIdx !== -1) {
        item.trackingFlatsteelExcel = responseData[responseDataItemIdx];
      } else {
        item.trackingFlatsteelExcel = {
          finish: 0,
          meltShop: 0,
          requestedQuantity: 0,
          rollingFinish: 0,
          rollingMill: 0,
          shipment: 0,
          stockWarehouse: 0,
          transit: 0,
          openQuantity: 0
        } as ITrackingFlatSteel;
      }
    };

    items.forEach((item: IOpenOrderData) => {
      getItemTracking(response.data.items, item);
    });
  }

  // retorna os dados de tracking online
  async getTrackingLongSteel(
    customerId: string,
    salesOrder: number,
    salesOrderItem: number
  ): Promise<ITrackingLongSteel> {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.TRACKING_LONG_STEEL}?customer=${customerId}&salesOrderItem=${salesOrderItem}&salesOrder=${salesOrder}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers
    });

    return response.data.tracking[0];
  }

  private sortOpenOrdersItemsByQuantities(items: IOpenOrderData[]) {
    const orderItemsByQuantitiesField = (
      sortedItems: IOpenOrderData[],
      field: string
    ): IOpenOrderData[] => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return sortedItems.sort((a: any, b: any) => {
        if (
          a.quantities[field] &&
          a.quantities[field] !== '' &&
          Number(a.quantities[field]) > 0 &&
          (!b.quantities[field] ||
            b.quantities[field] === '' ||
            Number(b.quantities[field]) === 0)
        ) {
          return -1;
        } else if (
          (!a.quantities[field] ||
            a.quantities[field] === '' ||
            Number(a.quantities[field]) === 0) &&
          b.quantities[field] &&
          b.quantities[field] !== '' &&
          Number(b.quantities[field]) > 0
        ) {
          return 1;
        } else {
          return 0;
        }
      });
    };

    // POR FATURADOS - billedQuantity
    items = orderItemsByQuantitiesField(items, 'billedQuantity');

    // POR REMESSA - deliveryQuantity
    items = orderItemsByQuantitiesField(items, 'deliveryQuantity');

    // POR CARREGAMENTO - shipmentQuantity
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    items = orderItemsByQuantitiesField(items, 'shipmentQuantity');
  }

  private sortFutureDeliveriesByEstimatedDeliveryDate(items: IOpenOrderData[]) {
    const getDateTime = (date: string): number => {
      const dateFormat = 'DD/MM/YYYY';
      let dateTime = 0;
      if (date.length > 10) {
        // caso retorne no formato "DD/MM/YYYY à DD/MM/YYYY"
        const splitedDate = date.split(' ');
        dateTime = moment(splitedDate[splitedDate.length - 1], dateFormat)
          .toDate()
          .getTime();
      } else {
        dateTime = moment(date, dateFormat).toDate().getTime();
      }
      return dateTime;
    };

    items.forEach((orderItem) => {
      orderItem.futureDeliveries.sort(
        (a: IFutureDelivery, b: IFutureDelivery) => {
          const dateA = a.estimatedDeliveryDate;
          const dateB = b.estimatedDeliveryDate;

          if (dateA && dateB) {
            return getDateTime(dateA) - getDateTime(dateB);
          } else if (dateA && !dateB) {
            return -1;
          } else if (!dateA && dateB) {
            return 1;
          } else {
            return 0;
          }
        }
      );
    });
  }

  private getReceivers(orders: ILongSteelOrderData[]): ICustomer[] {
    const receivers: ICustomer[] = [];

    orders.forEach((invoice) => {
      const isAlreadyAdd = receivers.find(
        (receiver) => receiver.customerId === invoice.shipTo.code
      );
      if (!isAlreadyAdd) {
        receivers.push({
          customerId: invoice.shipTo.code,
          name: invoice.shipTo.name,
          taxNumber: invoice.shipTo.taxNumber,
          locality: {
            address: invoice.shipTo.address,
            city: invoice.shipTo.city,
            district: invoice.shipTo.neighborhood,
            state: invoice.shipTo.state,
            country: invoice.shipTo.country,
            zipCode: invoice.shipTo.zipCode,
            zipcode: invoice.shipTo.zipCode
          },
          salesArea: [],
          division: []
        });
      }
    });

    return receivers;
  }

  private getEmitters(orders: ILongSteelOrderData[]): ICustomer[] {
    const emitters: ICustomer[] = [];

    orders.forEach((invoice) => {
      const isAlreadyAdd = emitters.find(
        (receiver) => receiver.customerId === invoice.soldTo.code
      );
      if (!isAlreadyAdd) {
        emitters.push({
          customerId: invoice.soldTo.code,
          name: invoice.soldTo.name,
          taxNumber: invoice.shipTo.taxNumber,
          locality: {
            address: invoice.soldTo.address,
            city: invoice.soldTo.city,
            district: invoice.soldTo.neighborhood,
            state: invoice.soldTo.state,
            country: invoice.soldTo.country,
            zipCode: invoice.soldTo.zipCode,
            zipcode: invoice.soldTo.zipCode
          },
          salesArea: [],
          division: []
        });
      }
    });

    return emitters;
  }

  private getDivisionsFromOpenOrders(
    orders: IOpenOrderData[]
  ): IOrderDivision[] {
    const divisions: IOrderDivision[] = [];
    orders.forEach((order) => {
      if (!divisions.find((div) => div.division === order.division)) {
        divisions.push({
          division: order.division,
          divisionDescription: order.divisionDescription
        });
      }
    });

    return divisions;
  }

  private getDivisionsFromBilledOrders(
    orders: IBilledOrderData[]
  ): IOrderDivision[] {
    const divisions: IOrderDivision[] = [];
    orders.forEach((order) => {
      if (
        !divisions.find((div) => div.division === order.billingHistory.division)
      ) {
        divisions.push({
          division: order.billingHistory.division,
          divisionDescription: order.billingHistory.divisionDescription
        });
      }
    });

    return divisions;
  }

  private setMinMaxDateInOpenOrders(ordersResponse: ILongSteelOrdersResponse) {
    // Ordena por data crescente
    (ordersResponse.items as IOpenOrderData[]).sort(
      (a, b) =>
        moment(a.requestedDate, 'YYYY-MM-DD').toDate().getTime() -
        moment(b.requestedDate, 'YYYY-MM-DD').toDate().getTime()
    );

    if (ordersResponse.items.length > 0) {
      ordersResponse.minOrderDate = (
        ordersResponse.items as IOpenOrderData[]
      )[0].requestedDate;
      ordersResponse.maxOrderDate = (ordersResponse.items as IOpenOrderData[])[
        ordersResponse.items.length - 1
      ].requestedDate;
    }
  }

  private setCanDownloadQC(orders: IBilledOrderData[]) {
    const canDownloadQC = (item: IBilledOrderData): boolean => {
      const plant = item.billingHistory.plant;
      const division = item.billingHistory.division;
      const salesOrganization = item.billingHistory.salesOrganization;
      const adminSku = item.billingHistory.loteAdminSKU;
      const brcgPlantsNotAllowed = ['24'];
      const plantsNotAllowed = [
        '60',
        '62',
        '63',
        '64',
        '69',
        '70',
        '71',
        '73',
        '74',
        '75'
      ];
      if (
        !this.unleash.isEnabled(
          `${process.env?.REACT_APP_BASE_URL}_cg_certificado_qualidade`
        )
      ) {
        plantsNotAllowed.push('44', '345');
      }
      //debbuger;
      if ('BRCG' == salesOrganization) {
        for (const na of brcgPlantsNotAllowed) {
          if (division == na) {
            return false;
          }
        }
      }

      for (const ap of plantsNotAllowed) {
        if (plant.startsWith(ap) || adminSku !== 'X') {
          return false;
        }
      }

      if (brcgPlantsNotAllowed.includes(plant.slice(0, 2))) {
        return false;
      }
      return true;
    };

    orders.forEach((item) => {
      item.qcState = {
        canDownloadQC: canDownloadQC(item)
      };
    });
  }

  private readonly setCanDownloadNF = (orders: IBilledOrderData[]) => {
    const unavailableText = 'Indisponível';

    const canDownloadNF = (itemConditions: IBilledOrderConditions) => {
      return (
        itemConditions.unitPriceWithoutTaxes !== unavailableText &&
        itemConditions.invoiceItemValueWithoutTaxes !== unavailableText &&
        itemConditions.invoiceItemValueWithTaxes !== unavailableText &&
        itemConditions.invoiceTotalValueWithoutTaxes !== unavailableText &&
        itemConditions.invoiceTotalValueWithTaxes !== unavailableText
      );
    };

    orders.forEach((item) => {
      item.nfState = {
        canDownloadNF: canDownloadNF(item.conditions)
      };
    });
  };

  private startInitialInputValues(
    ordersResponse: ILongSteelOrdersResponse,
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    tab: EOrderTab,
    startDate: string,
    endDate: string
  ) {
    if (tab === EOrderTab.OPEN) {
      this.setMinMaxDateInOpenOrders(ordersResponse);
    } else if (tab === EOrderTab.BILLED) {
      ordersResponse.minOrderDate = '';
      ordersResponse.maxOrderDate = '';

      // Ordena por data descrescente
      (ordersResponse.items as IBilledOrderData[]).sort(
        (a, b) =>
          moment(b.itemBillingDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(a.itemBillingDate, 'YYYY-MM-DD').toDate().getTime()
      );
    }

    const selectedEmitter =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerIds
        : ordersResponse.emitters && ordersResponse.emitters.length === 1
        ? [ordersResponse.emitters[0].customerId]
        : [];

    const selectedReceiver =
      customerViewType === ECustomerTypeView.EMITTER
        ? ordersResponse.receivers && ordersResponse.receivers.length === 1
          ? ordersResponse.receivers[0].customerId
          : ''
        : customerIds[0];

    ordersResponse.initialInputValues = {
      states: states,
      emitterIds: selectedEmitter,
      receiverId: selectedReceiver,
      orderStatus: ELongSteelOrdersStatus.ALL,
      startDate:
        tab === EOrderTab.OPEN && ordersResponse.minOrderDate
          ? ordersResponse.minOrderDate
          : startDate,
      endDate:
        tab === EOrderTab.OPEN && ordersResponse.maxOrderDate
          ? ordersResponse.maxOrderDate
          : endDate,
      tab
    };
  }

  async loadNFEFile(docNum: string): Promise<IDownloadNFEResponse> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.GENERATE_BILLING}?docNum=${docNum}&businessType=long-steel`,
      { headers }
    );

    return response.data as IDownloadNFEResponse;
  }

  async loadShippingNoteFile(
    deliveryDocumentZvoi: string
  ): Promise<IDownloadNFEResponse> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.GENERATE_BILLING}?deliveryDocument=${deliveryDocumentZvoi}&businessType=long-steel`,
      { headers }
    );

    return response.data as IDownloadNFEResponse;
  }

  async loadQCFile(
    cqNumber: string,
    deliveryCode: string,
    deliveryItemCode: string
  ): Promise<IQCFileResponse> {
    const headers = new EGerdauService().getHeaders();

    const downloadQCresponse = await customersAuthorizationApi.get(
      `${
        ECustomerAuthorizationEndpoints.DOWNLOAD_CQ_FILE
      }?qcNumber=${cqNumber}&deliveryNumber=${deliveryCode}&deliveryItemNumber=${deliveryItemCode.replace(
        /^0+/,
        ''
      )}`,
      { headers }
    );

    const qcFileResponse = downloadQCresponse.data as IQCFileResponse;

    if (
      !qcFileResponse.qcFile.qcBase64File ||
      qcFileResponse.qcFile.qcBase64File === 'Certificado não encontrado'
    ) {
      const generateCQresponse = await customersAuthorizationApi.get(
        `${
          ECustomerAuthorizationEndpoints.GENERATE_CQ
        }?delivery=${deliveryCode}&item=${deliveryItemCode.replace(/^0+/, '')}`,
        { headers }
      );

      qcFileResponse.qcFile.qcBase64File = generateCQresponse.data.FILE;
    }

    return qcFileResponse;
  }

  static async downloadMultipleCQs(
    orders: IBilledOrderData[]
  ): Promise<IQCFileResponse> {
    const headers = new EGerdauService().getHeaders();
    const payload = orders.map((order) => {
      return {
        qcNumber: order.cqNumber,
        deliveryNumber: order.deliveryCode,
        deliveryItemNumber: order.deliveryItemCode
      };
    });
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.MULTIPLE_CQ,
      [...payload],
      { headers }
    );
    return response.data as IQCFileResponse;
  }

  static async DownloadReportOrders(date: string): Promise<string> {
    const headers = new EGerdauService().getHeaders();
    const downloadCQresponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.REPORT_ORDERS_DOWNLOAD}/${date}`,
      { headers }
    );

    const base64File: string = downloadCQresponse.data;
    return base64File;
  }

  static async validationToUpdate(
    orders: IUpdateOrderValidation[]
  ): Promise<IUpdateOrderValidationResponse[]> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.VALIDATE_ORDERS_TO_UPDATE,
      orders,
      { headers }
    );
    return response.data;
  }

  static async changeOrderData(
    orders: IOrdersChangeData[]
  ): Promise<IOrdersChageDataResponse[]> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.ORDER_CHANGE_DATA,
      orders,
      { headers }
    );
    return response.data;
  }

  static async sendOrderUpdateData(
    orders: IUpdateOrderRequest[]
  ): Promise<IUpdateOrderResponse[]> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.patch(
      ECustomerAuthorizationEndpoints.EDIT_ITEMS,
      { items: orders },
      { headers }
    );
    return response.data.items;
  }

  async solicitateCgCertificate(
    item: IBilledOrderData
  ): Promise<ICgCertificateResponse> {
    const headers = new EGerdauService().getHeaders();
    const body: ICgCertificateRequest = {
      code: item.soldTo.code,
      deliveryCode: item.deliveryCode,
      deliveryItemCode: item.deliveryItemCode,
      invoiceNumber: item.invoiceNumber,
      plant: item.billingHistory.plant,
      salesOrder: item.billingHistory.salesOrder.toString().padStart(10, '0'),
      salesOrderItem: item.billingHistory.salesOrderItem
        .toString()
        .padStart(6, '0'),
      sku: item.billingHistory.sku.toString(),
      statusQC: item.billingHistory.statusQC ?? CgCertificateStatus.Available,
      product: item.billingHistory.materialDescription,
      productionBatch: item.productionBatch
    };
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.DELIVERY_QC,
      [body],
      { headers }
    );
    return response.data;
  }
}
